import React, { useState } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  Paper,
  Button,
  useTheme,
  Tooltip
} from '@mui/material';
import { industries } from '../types';

interface SidebarProps {
  activeIndustry: string;
  onIndustryChange: (industryId: string) => void;
  isMobile?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ activeIndustry, onIndustryChange, isMobile }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const handleIndustryClick = (industryId: string) => {
    onIndustryChange(industryId);
  };

  const toggleSidebar = () => {
    if (!isMobile) {
      setIsCollapsed(!isCollapsed);
    }
  };

  const currentCollapsed = isMobile ? false : isCollapsed;

  return (
    <Paper
      elevation={3}
      sx={{
        width: currentCollapsed ? 80 : 280,
        height: '100vh',
        backgroundColor: isDarkMode ? '#1e1e1e' : '#ffffff',
        borderRight: '1px solid',
        borderColor: isDarkMode ? '#333333' : '#e0e0e0',
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.3s ease',
        position: 'sticky',
        top: 0,
        left: 0,
        overflow: 'hidden',
        borderRadius: 0,
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          p: currentCollapsed ? 1.5 : 3,
          backgroundColor: isDarkMode ? '#1b5e20' : '#2e7d32',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: currentCollapsed ? 'center' : 'space-between',
          transition: 'all 0.3s ease',
          boxSizing: 'content-box',
        }}
      >
        {!currentCollapsed && (
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              行业分类
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
              选择行业查看技术路线
            </Typography>
          </Box>
        )}
        {!isMobile && (
          <Button
            onClick={toggleSidebar}
            sx={{
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
              minWidth: 'auto',
              px: 1,
            }}
          >
            {currentCollapsed ? '展开' : '收起'}
          </Button>
        )}
      </Box>

      <List sx={{ flex: 1, py: 2, overflow: 'auto', minHeight: 0 }}>
        {industries.map((industry) => {
          const isSelected = activeIndustry === industry.id;

          return (
            <React.Fragment key={industry.id}>
              <ListItem disablePadding sx={{ mb: 1 }}>
                <Tooltip 
                  title={currentCollapsed ? `${industry.name}\n${industry.description}` : ''} 
                  placement="right"
                  arrow
                >
                  <ListItemButton
                    onClick={() => handleIndustryClick(industry.id)}
                    selected={isSelected}
                    sx={{
                      mx: 2,
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      justifyContent: currentCollapsed ? 'center' : 'flex-start',
                      px: currentCollapsed ? 1.5 : 2,
                      '&.Mui-selected': {
                        backgroundColor: isDarkMode ? '#2e7d3230' : '#2e7d3215',
                        borderLeft: currentCollapsed ? 'none' : '4px solid',
                        borderColor: isDarkMode ? '#66bb6a' : '#2e7d32',
                        '&:hover': {
                          backgroundColor: isDarkMode ? '#2e7d3240' : '#2e7d3225',
                        },
                      },
                      '&:hover': {
                        backgroundColor: isDarkMode ? '#333333' : '#f5f5f5',
                      },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: isSelected ? 600 : 400,
                            color: isSelected
                              ? (isDarkMode ? '#66bb6a' : '#2e7d32')
                              : (isDarkMode ? '#ffffff' : '#212121'),
                          }}
                        >
                          {currentCollapsed ? industry.name.slice(0, 2) : industry.name}
                        </Typography>
                      }
                      secondary={
                        currentCollapsed ? undefined : (
                          <Typography
                            variant="caption"
                            sx={{
                              color: isDarkMode ? '#b0b0b0' : '#757575',
                              display: 'block',
                              mt: 0.5,
                            }}
                          >
                            {industry.description}
                          </Typography>
                        )
                      }
                    />
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            </React.Fragment>
          );
        })}
      </List>

      <Box
        sx={{
          p: 2,
          borderTop: '1px solid',
          borderColor: isDarkMode ? '#333333' : '#e0e0e0',
          backgroundColor: isDarkMode ? '#1a1a1a' : '#f8f9fa',
          flexShrink: 0,
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Typography 
          variant="caption" 
          sx={{ 
            display: 'block', 
            textAlign: 'center',
            color: isDarkMode ? '#b0b0b0' : 'text.secondary',
            opacity: currentCollapsed ? 0 : 1,
            transition: 'opacity 0.3s ease',
          }}
        >
          © 2026 Paperfancy
        </Typography>
      </Box>
    </Paper>
  );
};

export default Sidebar;
